import { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import type { Checkpoint, OperationTypeDefinition, LinkedEntity } from '@/graph/domain/types';
import type { AddStepFormState } from './types';
import type { RecommendedInstrumentRef } from './types';
import { ParameterField } from './ParameterField';
import { CheckpointList } from './CheckpointList';
import { DurationField } from './DurationField';
import { YieldField } from './YieldField';
import { AddCheckpointDialog } from './AddCheckpointDialog';
import { useGraphEditorStore } from '@/graph-store';
import type { EntityExplorerConfig, EntityExplorerResult } from '@/types/entity-explorer.types';
import {
  getRecommendedInstrumentOptions,
  type RecommendedInstrumentOption,
} from '@services/runApi.service';

const MAX_RECOMMENDED_INSTRUMENTS = 5;
const RECOMMENDED_INSTRUMENT_AUTOCOMPLETE_ID = 'recommended-instrument-autocomplete';

interface StepDetailsPanelProps {
  operationType: OperationTypeDefinition | null;
  form: AddStepFormState;
  onChange: (patch: Partial<AddStepFormState>) => void;
  onParamChange: (key: string, value: unknown) => void;
  validationFocusTarget?: StepValidationFocusTarget | null;
  validationFocusRequestId?: number;
  uniqueFieldErrors?: Record<string, string>;
  uniqueFieldPending?: Record<string, boolean>;
}

export type StepValidationFocusTarget =
  | { kind: 'parameter'; parameterName: string; fieldPath?: string }
  | { kind: 'duration' };

export function StepDetailsPanel({
  operationType,
  form,
  onChange,
  onParamChange,
  validationFocusTarget,
  validationFocusRequestId,
  uniqueFieldErrors = {},
  uniqueFieldPending = {},
}: StepDetailsPanelProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [recommendedOptions, setRecommendedOptions] = useState<RecommendedInstrumentOption[]>([]);
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [recommendedOpen, setRecommendedOpen] = useState(false);
  const paramSectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const durationSectionRef = useRef<HTMLDivElement | null>(null);
  const recommendedFieldRef = useRef<HTMLDivElement | null>(null);

  const openModal = useGraphEditorStore((s) => s.openModal);

  useEffect(() => {
    let cancelled = false;

    const loadInstruments = async () => {
      setRecommendedLoading(true);
      try {
        const options = await getRecommendedInstrumentOptions();
        if (!cancelled) {
          setRecommendedOptions(options);
        }
      } catch {
        if (!cancelled) {
          setRecommendedOptions([]);
        }
      } finally {
        if (!cancelled) {
          setRecommendedLoading(false);
        }
      }
    };

    void loadInstruments();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!validationFocusTarget) return;

    let target: HTMLDivElement | null = null;
    if (validationFocusTarget.kind === 'parameter') {
      target = paramSectionRefs.current[validationFocusTarget.parameterName] ?? null;
    } else if (validationFocusTarget.kind === 'duration') {
      target = durationSectionRef.current;
    }

    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    let focusable: HTMLElement | null = null;

    if (
      validationFocusTarget.kind === 'parameter' &&
      validationFocusTarget.fieldPath
    ) {
      const path = validationFocusTarget.fieldPath;
      const specificContainer = target.querySelector<HTMLElement>(`[data-validation-path="${path}"]`);
      if (specificContainer) {
        focusable = specificContainer.querySelector<HTMLElement>('input, select, textarea, button, [tabindex]');
      }
    }

    if (!focusable) {
      focusable = target.querySelector<HTMLElement>('input, select, textarea, button, [tabindex]');
    }

    focusable?.focus();
  }, [validationFocusTarget, validationFocusRequestId]);

  useEffect(() => {
    if (!recommendedOpen) return;

    const handleScrollOutside = (event: Event) => {
      const root = recommendedFieldRef.current;
      const target = event.target as EventTarget | null;
      if (!root || !target) return;

      const targetNode = target as Node;
      if (root.contains(targetNode)) {
        return;
      }

      const targetElement = target as Element;
      const isInsideRecommendedDropdown =
        typeof targetElement?.closest === 'function' &&
        (
          Boolean(targetElement.closest('[data-recommended-instrument-listbox="true"]')) ||
          Boolean(targetElement.closest('[data-recommended-instrument-popper="true"]')) ||
          Boolean(targetElement.closest(`#${RECOMMENDED_INSTRUMENT_AUTOCOMPLETE_ID}-listbox`)) ||
          Boolean(targetElement.closest(`#${RECOMMENDED_INSTRUMENT_AUTOCOMPLETE_ID}-popup`))
        );

      if (!isInsideRecommendedDropdown) {
        setRecommendedOpen(false);
      }
    };

    document.addEventListener('scroll', handleScrollOutside, true);
    return () => {
      document.removeEventListener('scroll', handleScrollOutside, true);
    };
  }, [recommendedOpen]);

  if (!operationType) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography color="text.secondary">Select an operation from the left panel</Typography>
      </Box>
    );
  }

  // Checkpoints live in form state (seeded from operationType when an op is selected).
  // Fall back to operationType.checkpoints for the initial render before form is populated.
  const checkpoints: Checkpoint[] = form.checkpoints.length > 0
    ? form.checkpoints
    : operationType.checkpoints;

  const existingNames = checkpoints.map((cp) => cp.checkpointName);

  const handleAddCheckpoint = (cp: Checkpoint) => {
    const updated = editingIndex !== null
      ? checkpoints.map((c, i) => (i === editingIndex ? { ...cp, isCustom: c.isCustom ?? true } : c))
      : [...checkpoints, { ...cp, isCustom: true }];
    onChange({ checkpoints: updated });
    setDialogOpen(false);
    setEditingIndex(null);
  };

  const handleEditCheckpoint = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleDeleteCheckpoint = (index: number) => {
    onChange({ checkpoints: checkpoints.filter((_, i) => i !== index) });
  };

  const handleOpenAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const sortedParams = [...operationType.parameters].sort((a, b) => a.order - b.order);
const unwrapStoredParamValue = (
  raw: unknown,
  depth = 0,
): unknown => {
  if (depth > 10) return raw;

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return raw;
  }

  const obj = raw as Record<string, unknown>;

  if (
    ('parameterId' in obj || 'parameterName' in obj) &&
    'value' in obj
  ) {
    return unwrapStoredParamValue(obj.value, depth + 1);
  }

  return raw;
};

  const getParamValue = (param: (typeof sortedParams)[number]) => {
    // Read from canonical parameterName, fallback to legacy shapes for compatibility
    if (form.parameterValues[param.parameterName] !== undefined) return unwrapStoredParamValue(form.parameterValues[param.parameterName]);
    if (form.parameterValues[param.id] !== undefined) return unwrapStoredParamValue(form.parameterValues[param.id]);
    if (param.safeKey && form.parameterValues[param.safeKey] !== undefined) return unwrapStoredParamValue(form.parameterValues[param.safeKey]);
    return '';
  };

  const focusedParameterName = validationFocusTarget?.kind === 'parameter'
    ? validationFocusTarget.parameterName
    : null;
  const focusedFieldPath = validationFocusTarget?.kind === 'parameter'
    ? (validationFocusTarget.fieldPath || validationFocusTarget.parameterName)
    : null;
  const isDurationFocused = validationFocusTarget?.kind === 'duration';

  const optionById = new Map(recommendedOptions.map((item) => [item.value, item]));
  const selectedRecommendedOptions = form.recommendedInstrument.map((saved) => {
    const match = optionById.get(saved.value);
    if (match) return match;
    return {
      _id: saved.value,
      value: saved.value,
      label: saved.value,
      name: saved.value,
      workspaceId: saved.workspaceId,
    } as RecommendedInstrumentOption;
  });

  const toInstrumentRefs = (items: RecommendedInstrumentOption[]): RecommendedInstrumentRef[] =>
    items.slice(0, MAX_RECOMMENDED_INSTRUMENTS).map((item) => ({
      value: item.value,
      workspaceId: item.workspaceId || operationType.workspaceId,
    }));

  const selectedRecommendedIds = new Set(selectedRecommendedOptions.map((item) => item.value));

  const inputHighlightSx = {
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ef4444 !important',
      borderWidth: '2px',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ef4444 !important',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ef4444 !important',
    },
  } as const;

  const getStatusBadgeSx = (status?: string) => {
    const normalized = String(status || '').toUpperCase().trim();

    if (normalized === 'AVAILABLE') {
      return { backgroundColor: '#d4edda', color: '#155724' };
    }
    if (normalized === 'OBSOLETE' || normalized.includes('INACTIVE') || normalized.includes('RETIRED')) {
      return { backgroundColor: '#f8d7da', color: '#721c24' };
    }
    if (normalized.includes('MAINTENANCE') || normalized.includes('INSTALLATION') || normalized === 'NOT FUNCTIONAL') {
      return { backgroundColor: '#fff3cd', color: '#856404' };
    }
    if (normalized.includes('CALIBRATION')) {
      return { backgroundColor: '#d1ecf1', color: '#0c5460' };
    }

    return { backgroundColor: '#e2e8f0', color: '#4a5568' };
  };

  return (
    <Box sx={{ p: 2, overflowY: 'auto', height: '100%' }} data-testid="step-details-panel">

      {/* ── Section heading ─────────────────────────────────────── */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#204c77' }}>
        Step Details
      </Typography>

      {/* ── Step Name ─────────────────────────────────────────────
           Mirrors Angular's <h6>Step Name :</h6> + {{ typeChosen?.name }} */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.25 }}>
          Step Name
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {operationType.operationName}
        </Typography>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* ── Yield (if allowed) ────────────────────────────────────── */}
      {operationType.operationTypeConfig?.allowYield && (
        <>
          <YieldField
            amount={form.yieldValue ?? 0}
            isPercent={form.yieldIsPercent}
            onChange={onChange}
          />
          <Divider sx={{ my: 1.5 }} />
        </>
      )}

      {/* ── Parameters ────────────────────────────────────────────
           Mirrors Angular's <h6>Parameters</h6> + app-step-parameters */}
      <Box sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#204c77' }}>
          Parameters
        </Typography>
        {sortedParams.length === 0 ? (
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            No parameters defined for this operation.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {sortedParams.map((param) => (
              (() => {
                const isInsideFocusedParameter = Boolean(
                  focusedFieldPath && (
                    focusedFieldPath === param.parameterName ||
                    focusedFieldPath.startsWith(`${param.parameterName}.`) ||
                    focusedFieldPath.startsWith(`${param.parameterName}[`)
                  ),
                );
                const focusedPathForParameter = isInsideFocusedParameter ? focusedFieldPath : null;
                const highlightWholeParameter = focusedParameterName === param.parameterName && focusedPathForParameter === param.parameterName;

                return (
              <Box
                key={param.id}
                ref={(el: HTMLDivElement | null) => {
                  paramSectionRefs.current[param.parameterName] = el;
                }}
                sx={highlightWholeParameter ? inputHighlightSx : undefined}
              >
                <ParameterField
                  definition={param}
                  value={getParamValue(param)}
                  onChange={(v) => onParamChange(param.parameterName, v)}
                  focusedFieldPath={focusedPathForParameter}
                  uniquenessError={uniqueFieldErrors[param.parameterName]}
                  uniquenessPending={Boolean(uniqueFieldPending[param.parameterName])}
                  uniquenessErrors={uniqueFieldErrors}
                  uniquenessPendingMap={uniqueFieldPending}
                  linkedEntities={form.linkedEntityParameters?.[param.parameterName] ?? []}
                  onLinkedEntitiesChange={(entities: LinkedEntity[]) => {
                    onChange({
                      ...form,
                      linkedEntityParameters: {
                        ...form.linkedEntityParameters,
                        [param.parameterName]: entities,
                      },
                    });
                  }}
                  onOpenModal={(config: any, callback: (result: any) => void) => {
                    openModal({
                      kind: 'entity-explorer',
                      payload: {
                        config: {
                          mode: 'import',
                          entityTypes: ['SPREADSHEETFILE'],
                          title: 'Link Spreadsheet',
                        multiple: true,
                        } as EntityExplorerConfig,
                        onConfirm: (result: EntityExplorerResult) => {
                          if (!result || result.kind !== 'import' || !result.items || result.items.length === 0) {
                            return;
                          }
                          const newEntities: LinkedEntity[] = result.items.map((item: any) => ({
                            entityId: item.entityId || item._id,
                            versionId: item._id,
                            name: item.name,
                            entityType: 'SPREADSHEETFILE_VERSION',
                          }));
                          const existing = form.linkedEntityParameters?.[param.parameterName] ?? [];
                          const merged = [...existing, ...newEntities].filter((e, i, arr) => arr.findIndex(x => x.entityId === e.entityId) === i);
                          onChange({
                            ...form,
                            linkedEntityParameters: {
                              ...form.linkedEntityParameters,
                              [param.parameterName]: merged,
                            },
                          });
                        },
                      },
                    });
                  }}
                />
              </Box>
                );
              })()
            ))}
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* ── Duration ──────────────────────────────────────────────
           Mirrors Angular's <h6>Duration</h6> + app-start-time */}
      <Box ref={durationSectionRef} sx={isDurationFocused ? inputHighlightSx : undefined}>
        <DurationField
          hours={form.durationHours}
          minutes={form.durationMinutes}
          seconds={form.durationSeconds}
          linkToPreviousStep={form.linkToPreviousStep}
          durationAllow={operationType.durationAllow !== false}
          durationRequired={operationType.durationRequired === true}
          onChange={onChange}
        />
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* ── Checkpoints ───────────────────────────────────────────
           Mirrors Angular's <h6>Checkpoints</h6> + Add Checkpoint button + list */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#204c77' }}>Checkpoints</Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAdd}>
            Add Checkpoint
          </Button>
        </Box>

        {checkpoints.length === 0 ? (
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', display: 'block', mb: 0.5 }}>
            No checkpoints added yet. Click "Add Checkpoint" to add one.
          </Typography>
        ) : (
          <CheckpointList
            checkpoints={checkpoints}
            canEdit={operationType.canEditCheckpoints}
            onEdit={(index) => handleEditCheckpoint(index)}
            onDelete={handleDeleteCheckpoint}
            workspaceId={operationType.workspaceId}
          />
        )}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* ── Recommended Instrument ────────────────────────────────
           Mirrors Angular's <h6>Recommended Instrument</h6> + multi-select */}
      <Box ref={recommendedFieldRef} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: '#204c77' }}>
          Recommended Instrument
        </Typography>
        <Autocomplete
          id={RECOMMENDED_INSTRUMENT_AUTOCOMPLETE_ID}
          multiple
          disableCloseOnSelect
          open={recommendedOpen}
          onOpen={() => setRecommendedOpen(true)}
          onClose={() => setRecommendedOpen(false)}
          slotProps={{
            listbox: { 'data-recommended-instrument-listbox': 'true' } as any,
            popper: { 'data-recommended-instrument-popper': 'true' } as any,
          }}
          options={recommendedOptions}
          value={selectedRecommendedOptions}
          loading={recommendedLoading}
          getOptionDisabled={(option) =>
            selectedRecommendedOptions.length >= MAX_RECOMMENDED_INSTRUMENTS && !selectedRecommendedIds.has(option.value)
          }
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label || option.name || option.value}
          onChange={(_, next) => onChange({ recommendedInstrument: toInstrumentRefs(next) })}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
              <Tooltip
                placement="right"
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: '#fff',
                      color: '#2d3748',
                      border: '1px solid #d0d9e8',
                      borderRadius: '8px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.08)',
                      p: 0,
                      minWidth: 265,
                      maxWidth: 265,
                    },
                  },
                }}
                title={
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        backgroundColor: '#f0f4fa',
                        borderBottom: '1px solid #d0d9e8',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.775rem',
                          fontWeight: 600,
                          color: '#1a4671',
                          maxWidth: 158,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {option.name || option.value}
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          ...getStatusBadgeSx(option.status),
                          fontSize: '0.64rem',
                          fontWeight: 600,
                          px: 0.9,
                          py: 0.2,
                          borderRadius: '10px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        {option.status || '-'}
                      </Box>
                    </Box>

                    <Box sx={{ px: 1.5, py: 1.1 }}>
                      <Box sx={{ display: 'flex', gap: 0.8, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>System ID</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, wordBreak: 'break-all' }}>{option.uid || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.8, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>Custom ID</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, wordBreak: 'break-all' }}>{option.externalUid || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.8, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>Type</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{option.instrumentType || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.8, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>Version</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, wordBreak: 'break-all' }}>{option.versionNumber || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.8 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>Storage Location</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, wordBreak: 'break-all' }}>{option.location || '-'}</Typography>
                      </Box>
                    </Box>
                  </Box>
                }
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {option.name || option.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {[option.uid, option.externalUid, option.collection].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Tooltip>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Select Recommended Instrument"
              helperText={`Select up to ${MAX_RECOMMENDED_INSTRUMENTS} instruments (${selectedRecommendedOptions.length}/${MAX_RECOMMENDED_INSTRUMENTS})`}
            />
          )}
        />
      </Box>

      {/* Dialog rendered outside scroll area */}
      <AddCheckpointDialog
        open={dialogOpen}
        checkpointToEdit={editingIndex !== null ? checkpoints[editingIndex] : null}
        existingNames={existingNames}
        operationTypeId={operationType.id}
        workspaceId={operationType.workspaceId}
        onConfirm={handleAddCheckpoint}
        onClose={() => { setDialogOpen(false); setEditingIndex(null); }}
      />
    </Box>
  );
}
